import React from 'react'

import CsvDownloadLink from 'components/CsvDownloadLink'
import { FileUpload } from 'components/FileUpload'
import { domains } from 'lib/config'
import Loading from 'components/Loading/Loading'
import {
  BROWSE,
  DOWNLOAD_RESULTS_LINK_LABEL,
  FIELD_DESCRIPTION_DISTRICT,
  FIELD_DESCRIPTION_EXTERNAL_SITE_ID,
  FIELD_DESCRIPTION_SITE_ID,
  TITLE,
  TOOL_DESCRIPTION,
  UPLOAD,
  csvTemplateHeaders,
} from './constants'
import {
  ExampleImage,
  FieldDescription,
  FieldDescriptionItem,
  FieldDescriptionList,
  FieldName,
  Instructions,
  Required,
  ResultsSection,
  StyledAutoTable,
  TemplateLinkWrapper,
  UploadButton,
  UploadSection,
} from './styles'

interface FormattedUploadResult {
  district?: string
  error?: string[]
  externalSiteId?: number | string
  siteId?: number | string
  siteName?: string
}

export interface ExternalSiteMappingProps {
  error?: string
  file?: File
  fileName: string
  formattedUploadResults?: FormattedUploadResult[]
  handleUpload: (file: File) => void
  isLoading: boolean
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void
}

interface Column {
  fieldValue: (row: FormattedUploadResult) => number | string | string[] | null
  sortValue?: string
  title: string
}

const columns: Column[] = [
  {
    title: 'Site ID',
    fieldValue: row => row.siteId,
    sortValue: 'siteId',
  },
  {
    title: 'External Site ID',
    fieldValue: row => row.externalSiteId,
    sortValue: 'externalSiteId',
  },
  {
    title: 'District',
    fieldValue: row => row.district,
    sortValue: 'district',
  },
  {
    title: 'Error',
    fieldValue: row => row.error?.join('; ') || null,
    sortValue: 'error',
  },
]

const FIELD_DESCRIPTIONS = [
  { name: 'Site ID:', description: FIELD_DESCRIPTION_SITE_ID },
  {
    name: 'External Site ID:',
    description: FIELD_DESCRIPTION_EXTERNAL_SITE_ID,
  },
  { name: 'District:', description: FIELD_DESCRIPTION_DISTRICT },
]

export const ExternalSiteMapping = (props: ExternalSiteMappingProps) => {
  const { file, fileName, isLoading, onFileChange, formattedUploadResults } =
    props

  return (
    <>
      <Loading isLoading={isLoading} message="Processing file...." />
      <h3>Tool Description</h3>
      <p>{TOOL_DESCRIPTION}</p>
      <hr />

      <h4>Fields Description</h4>
      <Required>All fields are required</Required>
      <FieldDescriptionList>
        {FIELD_DESCRIPTIONS.map(field => (
          <FieldDescriptionItem key={field.name}>
            <FieldName>{field.name}</FieldName>
            <FieldDescription>{field.description}</FieldDescription>
          </FieldDescriptionItem>
        ))}
      </FieldDescriptionList>
      <hr />
      <TemplateLinkWrapper>
        <CsvDownloadLink
          autoClick={false}
          data={[{}]}
          filename={`${TITLE}_template.csv`}
          headers={csvTemplateHeaders}
        >
          {`Download ${TITLE} Template`}
        </CsvDownloadLink>
      </TemplateLinkWrapper>

      <Instructions>
        Upload your <code>.xlsx</code> or <code>.csv</code> in the format below.
      </Instructions>
      <FileUpload
        disabled={isLoading}
        fileName={fileName}
        key={fileName}
        onFileChange={onFileChange}
      >
        {BROWSE}
      </FileUpload>
      <UploadSection>
        <UploadButton
          disabled={!file || isLoading}
          onClick={() => file && props.handleUpload(file)}
        >
          {UPLOAD}
        </UploadButton>
      </UploadSection>
      <ResultsSection>
        {formattedUploadResults && (
          <>
            <TemplateLinkWrapper>
              <CsvDownloadLink
                autoClick={false}
                data={formattedUploadResults}
                filename={new Date().toISOString() + `_${TITLE}_results.csv`}
                headers={csvTemplateHeaders}
              >
                {DOWNLOAD_RESULTS_LINK_LABEL}
              </CsvDownloadLink>
            </TemplateLinkWrapper>
            <StyledAutoTable
              borderless
              columns={columns}
              headThemeLight
              rows={formattedUploadResults}
            />
          </>
        )}
      </ResultsSection>
      <Instructions>
        All uploaded spreadsheets should be formatted like this:
      </Instructions>
      <ExampleImage
        src={`${domains.CDN_DOMAIN}/assets/img/externalsitemap_example.png`}
      />
    </>
  )
}
